import { Deal, DealStateEnum, DealStateLabelMap } from '../gql/dealGql';

import { valueOfDate } from './dates';

export type Swimlane = { [K in DealStateEnum]: Deal[] };

// Abbreviations to improve readability
const DSE = DealStateEnum;
const DSLMap = DealStateLabelMap;

export const allSwimlanes: {
  title: string;
  states: DealStateEnum[];
}[] = [
  { title: DSLMap[DSE.Estimate], states: [DSE.Estimate] }, // Floor is deprecated
  { title: DSLMap[DSE.SoftClose], states: [DSE.SoftClose] },
  {
    title: DSLMap[DSE.Structuring],
    states: [DSE.StructuringInProgress, DSE.Structuring],
  },
  { title: DSLMap[DSE.Closing], states: [DSE.Closing] },
  { title: 'Signing', states: [DSE.Closed, DSE.SentForSignatures] }, // Signing is a Swimlane but not a deal state
  { title: DSLMap[DSE.Closed], states: [DSE.Closed] },
  { title: DSLMap[DSE.SentForSignatures], states: [DSE.SentForSignatures] },
  { title: DSLMap[DSE.Signed], states: [DSE.Signed] },
  { title: DSLMap[DSE.Funded], states: [DSE.Funded] },
  { title: DSLMap[DSE.SendPayoff], states: [DSE.SendPayoff] },
  { title: DSLMap[DSE.WaitingForTitle], states: [DSE.WaitingForTitle] },
  { title: DSLMap[DSE.TitleReceived], states: [DSE.TitleReceived] },
  { title: DSLMap[DSE.SentToProcessor], states: [DSE.SentToProcessor] },
];

export type Direction = 'asc' | 'desc' | 'default';
export type SortOptionLabels = {
  asc?: string;
  desc: string;
};

export enum SortByEnum {
  DealAge = 'dealAge',
  Default = 'default',
  LastName = 'lastName',
  TimeInLane = 'timeInLane',
  UnassignedFS = 'unassignedFS',
}

export const SortByOptionLabels: Record<SortByEnum, SortOptionLabels> = {
  [SortByEnum.Default]: {
    desc: 'Default',
  },
  [SortByEnum.DealAge]: {
    asc: 'Deal Age · Oldest to Newest',
    desc: 'Deal Age · Newest to Oldest',
  },
  [SortByEnum.TimeInLane]: {
    asc: 'Time in Lane · Oldest to Newest',
    desc: 'Time in Lane · Newest to Oldest',
  },
  [SortByEnum.LastName]: {
    asc: 'Last Name · A to Z',
    desc: 'Last Name · Z to A',
  },
  [SortByEnum.UnassignedFS]: {
    desc: 'Unassigned FS First',
  },
};

export type SortOpt = {
  direction: Direction;
  type: SortByEnum;
};

export const groupDealsByState = (deals: Deal[]): Swimlane =>
  deals.reduce((sl, deal) => {
    if (sl[deal.state]) {
      sl[deal.state].push(deal); // Push the deal to the swimlane that corresponds to its state
    } else {
      // eslint-disable-next-line no-param-reassign
      sl[deal.state] = [deal]; // If the swimlane doesn't exist, create it
    }

    return sl;
  }, {} as Swimlane);

export const customSortDeals = (deals: Deal[], sortOpt?: SortOpt): Deal[] => {
  if (!sortOpt) {
    return deals;
  }
  const isAsc = sortOpt.direction === 'asc';

  return deals.sort((d1, d2) => {
    switch (sortOpt.type) {
      case SortByEnum.DealAge:
        if (!d1.created_at || !d2.created_at) {
          return 0;
        }
        return isAsc
          ? valueOfDate(d1.created_at) - valueOfDate(d2.created_at)
          : valueOfDate(d2.created_at) - valueOfDate(d1.created_at);

      case SortByEnum.LastName:
        if (!d1.customer?.last_name || !d2.customer?.last_name) {
          return 0;
        }
        return isAsc
          ? d1.customer.last_name.localeCompare(d2.customer.last_name)
          : d2.customer.last_name.localeCompare(d1.customer.last_name);

      case SortByEnum.TimeInLane:
        if (!d1.lane_arrival_utc || !d2.lane_arrival_utc) {
          return 0;
        }
        return isAsc
          ? valueOfDate(d1.lane_arrival_utc) - valueOfDate(d2.lane_arrival_utc)
          : valueOfDate(d2.lane_arrival_utc) - valueOfDate(d1.lane_arrival_utc);

      case SortByEnum.UnassignedFS:
        // Deals without closer_id (unassigned FS) come first
        // If both have or both don't have closer_id, maintain default sort order
        if (!d1.closer_id && d2.closer_id) {
          return -1;
        }
        if (d1.closer_id && !d2.closer_id) {
          return 1;
        }
        // If both have same assignment status, sort by default_sort_date
        return valueOfDate(d2.default_sort_date) - valueOfDate(d1.default_sort_date);

      default:
        return valueOfDate(d2.default_sort_date) - valueOfDate(d1.default_sort_date);
    }
  });
};
